import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { gsap, reducedMotion } from '@/lib/anim'
import { HelixStrand } from './Helix'

/** Shared dark page hero with a quiet helix in the margin. */
export default function PageHero({
  eyebrow,
  title,
  children,
  tall = false,
}: {
  eyebrow: string
  title: string
  children?: ReactNode
  tall?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || reducedMotion) return
    const items = el.querySelectorAll('[data-hero-in]')
    gsap.set(items, { opacity: 0, y: 36 })
    const tl = gsap.timeline({ delay: 0.15 })
    tl.to(items, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.14 })
    return () => {
      tl.kill()
    }
  }, [])

  return (
    <section
      className={`relative overflow-hidden ${tall ? 'min-h-[70vh]' : 'min-h-[58vh]'} flex items-end`}
      style={{ backgroundColor: '#24391C' }}
    >
      {/* fine helix in the right margin */}
      <div className="pointer-events-none absolute right-[6%] top-1/2 hidden -translate-y-1/2 lg:block">
        <HelixStrand height={560} width={110} turns={3} stroke="#8BA872" opacity={0.35} />
      </div>
      <div className="pointer-events-none absolute right-[13%] top-0 hidden h-full w-px bg-[#F3F5F1]/10 lg:block" />

      <div ref={ref} className="container-hx relative pb-20 pt-40 md:pb-24">
        <div data-hero-in className="eyebrow" style={{ color: '#8BA872' }}>
          {eyebrow}
        </div>
        <h1
          data-hero-in
          className="mt-6 max-w-2xl text-[clamp(2.2rem,4.6vw,3.5rem)] font-light leading-[1.08] tracking-[-0.02em] text-[#F3F5F1] [text-wrap:balance]"
        >
          {title}
        </h1>
        {children && (
          <div data-hero-in className="mt-7 max-w-xl text-base font-light leading-relaxed text-[#F3F5F1]/65">
            {children}
          </div>
        )}
      </div>
    </section>
  )
}
